// ============================================================================
// FinanceJournal Enums - Single Source of Truth
// ============================================================================

/**
 * Supported broker platforms
 */
export enum Broker {
  ZERODHA = 'Zerodha',
  GROWW = 'Groww',
  UPSTOX = 'Upstox',
  ANGELONE = 'Angel One',
  ICICI_DIRECT = 'ICICI Direct',
  HDFC_SECURITIES = 'HDFC Securities',
  KOTAK = 'Kotak',
}

/**
 * Human-readable broker names for display (same as enum values now)
 */
export const BrokerDisplayNames: Record<Broker, string> = {
  [Broker.ZERODHA]: 'Zerodha',
  [Broker.GROWW]: 'Groww',
  [Broker.UPSTOX]: 'Upstox',
  [Broker.ANGELONE]: 'Angel One',
  [Broker.ICICI_DIRECT]: 'ICICI Direct',
  [Broker.HDFC_SECURITIES]: 'HDFC Securities',
  [Broker.KOTAK]: 'Kotak',
};

/**
 * Broker logo/initial for display
 */
export const BrokerLogos: Record<Broker, string> = {
  [Broker.ZERODHA]: 'Z',
  [Broker.GROWW]: 'G',
  [Broker.UPSTOX]: 'U',
  [Broker.ANGELONE]: 'A',
  [Broker.ICICI_DIRECT]: 'I',
  [Broker.HDFC_SECURITIES]: 'H',
  [Broker.KOTAK]: 'K',
};

/**
 * Broker brand colors
 */
export interface BrokerColorScheme {
  bg: string;      // Background color (light)
  text: string;    // Text color
  border: string;  // Border color
}

export const BrokerColors: Record<Broker, BrokerColorScheme> = {
  [Broker.ZERODHA]: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
  },
  [Broker.GROWW]: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
  },
  [Broker.UPSTOX]: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
  },
  [Broker.ANGELONE]: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
  },
  [Broker.ICICI_DIRECT]: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },
  [Broker.HDFC_SECURITIES]: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200',
  },
  [Broker.KOTAK]: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
  },
};

/**
 * Helper function to get broker color classes
 */
export const getBrokerColors = (broker: Broker | string): BrokerColorScheme => {
  const colors = BrokerColors[broker as Broker];
  // Default colors if broker not found
  return colors || {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
  };
};

/**
 * Transaction type for trades
 */
export enum TransactionType {
  BUY = 'buy',
  SELL = 'sell',
}

/**
 * Position status in portfolio
 */
export enum PositionStatus {
  OPEN = 'open',
  CLOSED = 'closed',
}

/**
 * Timeline event types for journaling
 */
export enum TimelineEventType {
  BUY = 'buy',
  SELL = 'sell',
  NOTE = 'note',
  STOPLOSS_UPDATE = 'stoploss_update',
}

/**
 * Trigger source for stoploss updates
 */
export enum StoplossTrigger {
  BUY = 'buy',        // Stoploss set during a buy transaction (has trancheId)
  MANUAL = 'manual',  // User manually updated stoploss (no trancheId)
}

/**
 * Trade outcome status for journal
 */
export enum TradeOutcome {
  WIN = 'win',
  LOSS = 'loss',
}

/**
 * Category timeframe options
 */
export enum CategoryTimeframe {
  DAILY = 'Daily',
  WEEKLY = 'Weekly',
  BI_WEEKLY = 'Bi-weekly',
  MONTHLY = 'Monthly',
  QUARTERLY = 'Quarterly',
}

/**
 * Sort options for portfolio positions
 */
export enum SortOption {
  PNL_ABSOLUTE = 'pnl-abs',
  PNL_PERCENT = 'pnl-percent',
  PORTFOLIO_PERCENT = 'portfolio-percent',
  DURATION = 'duration',
  VALUE = 'value',
  RISK = 'risk',
  TO_SL = 'to-sl',
  DAILY_CHANGE = 'daily-change',
  WEEKLY_CHANGE = 'weekly-change',
  MONTHLY_CHANGE = 'monthly-change',
}

/**
 * Stock exchange identifiers
 */
export enum Exchange {
  NSE = 'NSE',
  BSE = 'BSE',
}
